import { delay } from "./delay";
import { isTestEnv } from "./env";
import { FetchError, logError } from "./errors";
import { isNotNullOrUndefined } from "./guard";
import { Guard, jsonParse } from "./validation";

export const fetchWithRetry = async (
  input: string,
  {
    retry,
    checkShouldRetry,
    backoff,
    ...options
  }: RequestInit & {
    checkShouldRetry?: (result: Response) => Promise<boolean> | boolean;
    retry?: number;
    backoff?: number;
  },
) => {
  let attempt = 1;
  let result = await fetch(input, options);

  if (!isTestEnv()) {
    while (
      (!result.ok ||
        (checkShouldRetry && (await checkShouldRetry(result.clone())))) &&
      attempt <= (retry || 0)
    ) {
      const delayed = (backoff || 500) * attempt;
      await delay(delayed);

      attempt += 1;
      result = await fetch(input, {
        ...options,
        headers: {
          attempt: `${attempt}`,
          ...options.headers,
        },
      });
    }
  }

  return result;
};

type AuthOptions =
  | { type: "basic"; username: string; password: string }
  | { type: "bearer"; token: string }
  | { type: "loader"; load: () => Promise<string> }
  | { type: "token"; token: string };

const buildAuthHeader = async (opts: AuthOptions) => {
  switch (opts.type) {
    case "basic":
      return `Basic ${Buffer.from(`${opts.username}:${opts.password}`).toString(
        "base64",
      )}`;
    case "bearer":
      return `Bearer ${opts.token}`;
    case "loader":
      return opts.load();
    case "token":
      return `Token ${opts.token}`;
    default:
      return "";
  }
};

export type GuardedFetchOptions = {
  urlPrefix?: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  auth?: AuthOptions;
  headers?: Record<string, string>;
  searchParams?: Record<string, string | number | boolean | undefined>;
  body?: FormData | URLSearchParams;
  json?: Record<string, any>;
  skipParsing?: boolean;
  retry429?: boolean;
};

const SECRET_KEY = /authorization|password|secret|token|api[-_]?key|^key$/i;

const maskSecrets = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(maskSecrets);
  if (value && Object.getPrototypeOf(value) === Object.prototype)
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [
        key,
        SECRET_KEY.test(key) ? "[redacted]" : maskSecrets(nested),
      ]),
    );
  return value;
};

const maskBody = (input: unknown) => {
  if (typeof input !== "string") return input;
  try {
    return JSON.stringify(maskSecrets(JSON.parse(input)));
  } catch {
    return input;
  }
};

const loggableOptions = ({
  auth,
  headers,
  searchParams,
  json,
  ...options
}: GuardedFetchOptions) => ({
  ...options,
  auth: auth && { type: auth.type },
  headers: maskSecrets(headers),
  searchParams: maskSecrets(searchParams),
  json: maskSecrets(json),
});

export const guardedFetch = async <E>(
  url: string,
  options: GuardedFetchOptions,
  guard: Guard<E>,
  action: string,
) => {
  try {
    let statusCode!: number;
    let input;
    let body;

    const maxRetry429 = options.retry429 ? 3 : 0;
    let attempt = 0;
    while (attempt <= maxRetry429) {
      if (attempt > 0) {
        // Jitter (±500ms) prevents concurrent lambdas from hitting the limit again together
        const backoffMs = 2 ** attempt * 1000 + Math.random() * 500;
        console.info(
          `Retrying after 429 (attempt ${attempt}/${maxRetry429}), waiting ${Math.round(backoffMs)}ms`,
        );
        await delay(backoffMs);
      }

      try {
        const headers = new Headers({
          ...options.headers,
        });

        if (options.auth)
          headers.set("Authorization", await buildAuthHeader(options.auth));

        const fullUrl = new URL(url, options.urlPrefix);
        if (options.searchParams) {
          Object.entries(options.searchParams || {}).forEach(([key, value]) => {
            if (isNotNullOrUndefined(value)) {
              fullUrl.searchParams.set(key, value.toString());
            }
          });
        }

        if (options.body instanceof URLSearchParams) {
          headers.set("Content-Type", "application/x-www-form-urlencoded");
          input = options.body.toString();
        } else if (options.body instanceof FormData) {
          input = options.body;
        } else if (options.json) {
          headers.set("Content-Type", "application/json; charset=utf-8");
          input = JSON.stringify(options.json);
        }

        const fetchOptions = {
          headers,
          method: options.method,
          body: input,
        };
        if (process.env.DEBUG)
          console.info(`Fetching ${fullUrl}`, {
            method: fetchOptions.method,
            body: fetchOptions.body,
            headers: maskSecrets(
              Object.fromEntries(fetchOptions.headers.entries()),
            ),
          });

        const fetched = await fetch(fullUrl, fetchOptions);
        statusCode = fetched.status;
        body = await fetched.text();
      } catch (e) {
        throw new FetchError(e, { action });
      }

      if (statusCode !== 429) break;
      attempt += 1;
    }

    if (statusCode >= 400) {
      throw new FetchError(
        {
          code: statusCode,
          message: "Unsuccessful Request",
          request: { requestUrl: url },
          options: { body: maskBody(input) },
          response: { statusCode, body },
        },
        { action },
      );
    }

    if (options.skipParsing) return body as E;

    const parsed = await jsonParse(body, `${action} parse`);
    const guarded = await guard(parsed, `${action} guard`);

    return guarded;
  } catch (e) {
    logError(e, { url, options: loggableOptions(options) });
    throw e;
  }
};

export type GuardedFetch = typeof guardedFetch;

export const guardedFetchFactory =
  (baseOptions: GuardedFetchOptions) =>
  async <E>(
    url: string,
    options: GuardedFetchOptions,
    guard: Guard<E>,
    action: string,
  ) =>
    guardedFetch(
      url,
      {
        ...baseOptions,
        ...options,
        headers: { ...baseOptions.headers, ...options.headers },
        searchParams: { ...baseOptions.searchParams, ...options.searchParams },
      },
      guard,
      action,
    );
